/**
 * Wave 78 · minimal i18n for the Vitrine.
 *
 * The Vitrine is pt-BR by default (per JP's locale memory). EN exists
 * for non-Brazilian buyers who land on the page via search; it's a
 * quality-of-life add, not the primary surface.
 *
 * We translate the high-leverage strings (Hero headlines, LeadForm
 * labels, Footer credits, Housy launcher) and let the prose-heavy
 * vignettes stay pt-BR. A buyer who reads the labels in EN but the
 * Approach paragraph in PT is still a buyer who got the message.
 *
 * Locale persists in localStorage under `casa:locale`. Default = pt-BR.
 * The hook is a plain pub-sub on a module-level state so we don't need
 * a context provider tree.
 */

import { useEffect, useState } from 'react';

export type Locale = 'pt-BR' | 'en';

const KEY = 'casa:locale';

function read(): Locale {
  if (typeof window === 'undefined') return 'pt-BR';
  const v = window.localStorage.getItem(KEY);
  return v === 'en' ? 'en' : 'pt-BR';
}

let current: Locale = read();
const subs = new Set<(l: Locale) => void>();

export function getLocale(): Locale { return current; }

export function setLocale(l: Locale) {
  current = l;
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(KEY, l);
    document.documentElement.lang = l === 'en' ? 'en' : 'pt-BR';
  }
  subs.forEach((s) => s(l));
}

export function useLocale(): [Locale, (l: Locale) => void] {
  const [l, set] = useState<Locale>(current);
  useEffect(() => {
    const sub = (next: Locale) => set(next);
    subs.add(sub);
    return () => { subs.delete(sub); };
  }, []);
  return [l, setLocale];
}

// Compact dictionary. Keys are namespaced by surface.
const dict: Record<string, { 'pt-BR': string; en: string }> = {
  // Hero
  'hero.eyebrow': { 'pt-BR': 'Projeto', en: 'Project' },
  'hero.scrollHint': { 'pt-BR': 'role para descobrir', en: 'scroll to explore' },

  // LeadForm
  'lead.eyebrow': { 'pt-BR': 'Acesso', en: 'Access' },
  'lead.title': { 'pt-BR': 'Quer conhecer por dentro?', en: 'Want to see it from the inside?' },
  'lead.body': {
    'pt-BR': "Mandamos os projetos completos — arquitetônico, estrutural, hidráulico, elétrico — e o memorial descritivo por link privado, válido por alguns dias. Deixe seu contato e o gestor da obra libera o acesso.",
    en: "We share the full project package — architectural, structural, hydraulic, electrical — plus the technical memorial via a private link, valid for a few days. Leave your contact and the project manager will grant access.",
  },
  'lead.fullName': { 'pt-BR': 'Nome completo', en: 'Full name' },
  'lead.email': { 'pt-BR': 'E-mail', en: 'Email' },
  'lead.phone': { 'pt-BR': 'Telefone / WhatsApp', en: 'Phone / WhatsApp' },
  'lead.message': { 'pt-BR': 'Mensagem', en: 'Message' },
  'lead.submit': { 'pt-BR': 'Solicitar acesso', en: 'Request access' },
  'lead.sent': { 'pt-BR': 'Recebido. O gestor da obra libera o acesso em breve.', en: 'Got it. The project manager will grant access shortly.' },
  'lead.required': { 'pt-BR': 'obrigatório', en: 'required' },
  'lead.optional': { 'pt-BR': 'opcional', en: 'optional' },

  // Public Housy
  'housy.launchOpen': { 'pt-BR': 'Pergunte sobre a casa', en: 'Ask about the house' },
  'housy.launchClose': { 'pt-BR': 'Fechar', en: 'Close' },
  'housy.title': { 'pt-BR': 'Concierge da casa', en: "House concierge" },
  'housy.placeholder': { 'pt-BR': 'Pergunte sobre área, materiais, bairro, status…', en: 'Ask about area, materials, neighborhood, status…' },
  'housy.send': { 'pt-BR': 'Enviar', en: 'Send' },

  // Footer
  'footer.builtBy': { 'pt-BR': 'Construído por', en: 'Built by' },
  'footer.poweredBy': { 'pt-BR': 'Plataforma', en: 'Platform' },
  // Wave 288 — extended coverage. Picked the strings that are visible
  // every page-load. Dynamic content (project copy, scene captions
  // loaded from DB) stays pt-BR until Wave 288.2 ships LLM-translated
  // cache. EN visitors see a hybrid page; that's a known compromise.
  'footer.about': { 'pt-BR': 'Sobre', en: 'About' },
  'footer.legal': { 'pt-BR': 'Legal', en: 'Legal' },
  'footer.rights': { 'pt-BR': 'Todos os direitos reservados.', en: 'All rights reserved.' },
  'footer.builtWithAi': { 'pt-BR': 'Construído com IA por', en: 'Built with AI by' },
  // Specs / ficha técnica
  'specs.title': { 'pt-BR': 'Ficha técnica', en: 'Technical sheet' },
  'specs.subtitle': { 'pt-BR': 'Os números da casa', en: 'The numbers behind the house' },
  'specs.area': { 'pt-BR': 'Área construída', en: 'Built area' },
  'specs.terrain': { 'pt-BR': 'Terreno', en: 'Lot' },
  'specs.suites': { 'pt-BR': 'Suítes', en: 'Suites' },
  'specs.bathrooms': { 'pt-BR': 'Banhos', en: 'Bathrooms' },
  'specs.parking': { 'pt-BR': 'Vagas', en: 'Parking' },
  // Timeline
  'timeline.title': { 'pt-BR': 'Linha do tempo', en: 'Timeline' },
  'timeline.subtitle': { 'pt-BR': 'Marcos da obra', en: 'Project milestones' },
  // Gallery
  'gallery.title': { 'pt-BR': 'Galeria', en: 'Gallery' },
  // Condo / location
  'condo.title': { 'pt-BR': 'Onde está', en: 'Where' },
  'condo.subtitle': { 'pt-BR': 'O bairro', en: 'The neighborhood' },
  // Common
  'common.loading': { 'pt-BR': 'Carregando', en: 'Loading' },
  'common.notFound': { 'pt-BR': 'Casa não encontrada.', en: 'House not found.' },
  // Lead form extra
  'lead.errorGeneric': { 'pt-BR': 'Não foi possível enviar. Tente de novo em alguns segundos.', en: 'We could not send your message. Please try again in a few seconds.' },
  // Variant divider (used to label rendering modes)
  'variant.material.label': { 'pt-BR': 'Em matéria', en: 'In matter' },
  'variant.line.label': { 'pt-BR': 'Em linha', en: 'In line' },
  'variant.material.hint': { 'pt-BR': 'Como a casa será construída — materiais, brises, vidros.', en: 'How the house will be built — materials, brises, glass.' },
  'variant.line.hint': { 'pt-BR': 'A planta — eixos, paredes, aberturas.', en: 'The floor plan — axes, walls, openings.' },
};

export function t(key: keyof typeof dict): string {
  return dict[key]?.[current] ?? key;
}

/**
 * Wave 308 · Dynamic-content translation hook.
 *
 * Pairs with `translateFields` in lib/api.ts. Renders the source map
 * immediately (so the page never waits on the translator) and swaps to
 * the translated map when ready. Re-fetches when locale changes.
 *
 * Use for content that lives in the DB / API response (showcase
 * headline, subheadline, lead message). Static labels stay in `t()`.
 *
 * Failure modes are silent — on any error we keep the source map so the
 * page is always readable.
 */
export function useTranslatedFields(
  slug: string,
  fields: Record<string, string>,
  fetchFn: (slug: string, fields: Record<string, string>, locale: string) => Promise<Record<string, string>>,
): Record<string, string> {
  const [locale] = useLocale();
  // Stringified field-set acts as the dependency key — recompose only
  // when the source actually changes (object identity changes per
  // render in the casa app).
  const fieldKey = JSON.stringify(fields);
  const [translated, setTranslated] = useState<Record<string, string>>(fields);

  useEffect(() => {
    let alive = true;
    // pt-BR is a no-op in the API helper, but we still want the state
    // to reflect the source.
    fetchFn(slug, fields, locale)
      .then((out) => { if (alive) setTranslated(out); })
      .catch(() => { if (alive) setTranslated(fields); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, locale, fieldKey]);

  return translated;
}
